/**
 * Investor API.
 *
 * The double gate — role 'investor' AND investor_accounts.approved_by_admin — guards
 * every financial surface. GET /investor/status is the one route outside it, so an
 * unapproved investor can learn they are unapproved without being shown anything
 * they are not entitled to.
 */

import { api } from './apiClient';

// ── Account status ──────────────────────────────────────────────────────────

export type AccreditationMethod = 'income' | 'net_worth' | 'professional' | 'entity';
export type InvestorState = 'accreditation_required' | 'under_review' | 'approved';

/** Reg D 506(c) categories, worded for a human rather than a statute. */
export const ACCREDITATION_OPTIONS: {
  value: AccreditationMethod; label: string; detail: string;
}[] = [
  {
    value: 'income',
    label: 'Income',
    detail: 'Individual income over $200,000 (or $300,000 jointly) in each of the last two years, with the same expected this year.',
  },
  {
    value: 'net_worth',
    label: 'Net worth',
    detail: 'Individual or joint net worth over $1,000,000, excluding the value of your primary residence.',
  },
  {
    value: 'professional',
    label: 'Professional certification',
    detail: 'You hold a Series 7, Series 65, or Series 82 licence in good standing.',
  },
  {
    value: 'entity',
    label: 'Entity',
    detail: 'An entity with over $5,000,000 in assets, or one in which all equity owners are accredited investors.',
  },
];

export interface InvestorStatus {
  investorAccountId: string;
  approved: boolean;
  approvedAt: string | null;
  accreditedStatus: boolean;
  accreditationMethod: AccreditationMethod | null;
  accreditationDocUrl: string | null;
  accreditationSubmittedAt: string | null;
  investorProvisioned: boolean;
  createdAt: string;
  state: InvestorState;
}

export const getInvestorStatus = () => api.get<InvestorStatus>('/investor/status');

export const submitAccreditation = (method: AccreditationMethod, docUrl?: string) =>
  api.put<{ submitted: boolean; state: InvestorState; note: string }>(
    '/investor/accreditation',
    { method, docUrl: docUrl || null },
  );

// ── Opportunities (approved investors only) ───────────────────────────────────

export interface Opportunity {
  id: string;
  title: string;
  description: string | null;
  eventTitle: string;
  featuredImageUrl: string | null;
  eventDate: string | null;
  pricePerShareCents: number;
  totalShares: number;
  sharesSold: number;
  minimumShares: number;
  investorPoolCents: number;
  preferredReturnPercentage: number;
  performanceFeePercentage: number;
  /** 0..1 */
  fundingProgress: number;
  daysRemaining: number | null;
  closesAt: string | null;
  complianceBadge: 'fully_permitted' | 'permit_pending';
  investorsCommitted: number;
  organizerTrackRecord: { pastEvents: number };
  wefunderOpportunityUrl: string | null;
}

export const listOpportunities = () => api.get<Opportunity[]>('/opportunities');
export const getOpportunity = (id: string) => api.get<Opportunity>(`/opportunities/${id}`);

// ── Portfolio ───────────────────────────────────────────────────────────────

export interface Portfolio { [key: string]: unknown }
export const getPortfolio = () => api.get<Portfolio>('/investor/portfolio');

export interface InvestmentRow {
  [key: string]: unknown;
  id?: string;
  investmentId?: string;
  status?: string;
}
export const listMyInvestments = () => api.get<InvestmentRow[]>('/investments/mine');

// ── The three-step flow ─────────────────────────────────────────────────────

export interface Quote {
  shares: number;
  pricePerShareCents: number;
  amountInvestedCents: number;
  /** 2.5% of the invested amount, computed server-side. */
  platformEntryFeeCents: number;
  totalCents: number;
}

export interface StartedInvestment {
  investmentId: string;
  contractNumber: string;
  quote: Quote;
}




export const contractUrl = (investmentId: string) =>
  `/api/investments/${investmentId}/contract`;
